<template>
  <div class="admin-information">
    <div class="admin-information__head" @click="changeOpen">
      {{ headLabel }}
    </div>
    <div v-if="isOpen" class="admin-information__body">
      <div
        v-for="(_item) in serviceCharacteristics"
        class="service-characteristics"
      >
        <div class="--label">{{ _item.label }}:</div>
        <div
          v-html="_item.value"
          class="--value"
        />
      </div>
      <a :href="linkProductEdit" class="link-page-edit-product" rel="nofollow noopener noreferrer" target="_blank">
        {{ editLinkLabel }}
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">

import type { IProduct } from '#sitis/internal/controllers/products/models/Product';
import type { ComputedRef } from 'vue';
import { getCharacteristicFromFull } from '~/utils/getCharacteristic';

interface IProps {
  product: IProduct | null
}
interface IServiceCharacteristic {
  label: string,
  value: string
}

const props = withDefaults(defineProps<IProps>(), {
  product: null
});
const runtimeConfig = useRuntimeConfig()

const isOpen: Ref<boolean> = ref(false);
const changeOpen = () => {
  isOpen.value = !isOpen.value;
};

const isCollection: ComputedRef<boolean> = computed(() => {
  return String(props.product?.isProductSet) === 'true';
});

const headLabel: ComputedRef<string> = computed(() => {
  return isOpen.value ? 'Свернуть' : 'Развернуть'
});
const editLinkLabel: ComputedRef<string> = computed(() => {
  return isCollection.value
    ? 'Открыть редактирование коллекции'
    : 'Открыть редактирование товара';
});
const serviceCharacteristics: ComputedRef<IServiceCharacteristic[]> = computed(() => {
  let _list: IServiceCharacteristic[] = [];

  if (props.product?.brand) {
    _list.push({ label: "Фабрика", value: props.product?.brand?.name });
  }

  const fabrikaDlyaCrm: string = getCharacteristicFromFull(props?.product?.characteristics, 'fabrika-dlya-crm')?.values?.[0]?.value || "";
  if (fabrikaDlyaCrm) {
    _list.push({ label: "Фабрика для CRM", value: fabrikaDlyaCrm });
  }

  const kollekciyaDlyaCrm: string = getCharacteristicFromFull(props?.product?.characteristics, 'kollekciya-dlya-crm')?.values?.[0]?.value || "";
  if (kollekciyaDlyaCrm) {
    _list.push({ label: "Коллекция для CRM", value: kollekciyaDlyaCrm });
  }

  if (props.product?.code) {
    _list.push({ label: "Артикул", value: props.product?.code });
  }
  if (!isCollection.value && props.product?.sku) {
    _list.push({ label: "Артикул фабрики", value: props.product?.sku });
  }

  const updatedAt: any = props.product?.updatedAt;
  if (updatedAt) {
    _list.push({ label: "Дата обновления", value: new Date(updatedAt * 1000).toLocaleDateString("ru", {
        day: '2-digit',
        month: "short",
        year: 'numeric'
      }) })
  }

  const kommentarijSluzhebnyj = getCharacteristicFromFull(props?.product?.characteristics, 'kommentarij-sluzhebnyj');
  if (!!kommentarijSluzhebnyj?.values?.[0]?.value) {
    _list.push({
      label: kommentarijSluzhebnyj?.name,
      value: kommentarijSluzhebnyj?.values?.[0]?.value
    });
  }

  const isCilekBrand = props.product?.brand?.url?.includes('mebel-cilek')
    || (props.product?.brand as any)?.slug === 'mebel-cilek';

  if (!isCollection.value && isCilekBrand) {
    const cilekMoyskladSlugs = [
      'ostatki-cilek-moysklad',
      'rezerv-cilek-moysklad',
      'dostupno-cilek-moysklad',
    ];

    cilekMoyskladSlugs.forEach((slug) => {
      const characteristic = getCharacteristicFromFull(props?.product?.characteristics, slug);
      if (characteristic?.values?.[0]?.value != null && characteristic?.values?.[0]?.value !== '') {
        _list.push({
          label: characteristic?.name || slug,
          value: String(characteristic.values[0].value),
        });
      }
    });
  }

  return _list
});

const linkProductEdit: ComputedRef<string> = computed(() => {
  return [runtimeConfig.public.urlReactAdmin, '/products/edit/', props.product?.id].join('')
});

</script>

<style scoped lang="scss">
.admin-information {}
.admin-information__head {
  font-weight: 500;
  font-size: 16px;
  line-height: 150%;
  text-decoration-line: underline;
  color: #ED908C;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    text-decoration-color: transparent;
  }
}
.admin-information__body {
  display: flex;
  flex-direction: column;
  margin-top: 12px;

  & > * {
    margin-top: 8px;
    &:first-child {
      margin-top: 0;
    }
  }
}

.service-characteristics {
  display: flex;
  font-size: 14px;
  line-height: 18px;
  color: #252525;

  .--value {
    flex: 1;
    font-weight: 600;
    margin-left: 8px;
  }
}
.link-page-edit-product {
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;
  text-decoration-line: underline;
  color: #ED908C;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    text-decoration-color: transparent;
  }
}
</style>
